import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const COLORS = {
  sensory: '#2493A2',
  integration: '#57BDA2',
  symbolic: '#DCB13C',
  capstone: '#E85D75',
};

interface Token {
  text: string;
  x: number;
  y: number;
}

const TOKENS: Token[] = [
  { text: 'tree', x: 30, y: 35 },
  { text: 'car', x: 110, y: 30 },
  { text: 'cloud', x: 210, y: 25 },
  { text: 'person', x: 270, y: 45 },
  { text: 'phone', x: 55, y: 75 },
  { text: 'problem', x: 155, y: 70 },
  { text: 'bird', x: 250, y: 85 },
  { text: 'shadow', x: 30, y: 115 },
  { text: 'deadline', x: 120, y: 110 },
  { text: 'breeze', x: 220, y: 120 },
  { text: 'noise', x: 285, y: 135 },
  { text: 'money', x: 60, y: 155 },
  { text: 'child', x: 160, y: 155 },
  { text: 'sign', x: 250, y: 165 },
  { text: 'opportunity', x: 100, y: 185 },
];

// Which groups the spotlight selects (indices into TOKENS)
const SPOTLIGHT_GROUPS = [
  { indices: [5, 8, 11], spotX: 120, spotY: 110, label: 'worry' },
  { indices: [3, 6, 12], spotX: 230, spotY: 100, label: 'people' },
  { indices: [1, 4, 14], spotX: 100, spotY: 130, label: 'opportunity' },
];

export function AttentionSpotlight() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    if (!svgRef.current) return;

    svg.selectAll('*').remove();

    const W = 320;
    const H = 200;

    // Defs: radial gradient for spotlight
    const defs = svg.append('defs');
    const spotGrad = defs.append('radialGradient').attr('id', 'spot-grad');
    spotGrad.append('stop').attr('offset', '0%').attr('stop-color', COLORS.symbolic).attr('stop-opacity', 0.25);
    spotGrad.append('stop').attr('offset', '70%').attr('stop-color', COLORS.symbolic).attr('stop-opacity', 0.08);
    spotGrad.append('stop').attr('offset', '100%').attr('stop-color', COLORS.symbolic).attr('stop-opacity', 0);

    // -- Token labels --
    const tokenGroup = svg.append('g');

    const tokenSels = TOKENS.map(token => {
      const g = tokenGroup.append('g')
        .attr('transform', `translate(${token.x}, ${token.y})`);

      const text = g.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em')
        .attr('fill', 'var(--color-text-muted)')
        .attr('font-size', '9px')
        .attr('opacity', 0.5)
        .text(token.text);

      // Highlight label (hidden by default)
      const highlight = g.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '-10')
        .attr('fill', COLORS.symbolic)
        .attr('font-size', '7px')
        .attr('font-weight', '600')
        .attr('opacity', 0)
        .text('');

      return { g, text, highlight, token };
    });

    // -- Spotlight circle --
    const spotlight = svg.append('circle')
      .attr('cx', W / 2)
      .attr('cy', H / 2)
      .attr('r', 65)
      .attr('fill', 'url(#spot-grad)')
      .attr('opacity', 0);

    // Spotlight ring
    const spotlightRing = svg.append('circle')
      .attr('cx', W / 2)
      .attr('cy', H / 2)
      .attr('r', 65)
      .attr('fill', 'none')
      .attr('stroke', COLORS.symbolic)
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3')
      .attr('opacity', 0);

    // Category label for current spotlight
    const categoryLabel = svg.append('text')
      .attr('x', W / 2)
      .attr('y', H / 2)
      .attr('text-anchor', 'middle')
      .attr('fill', COLORS.symbolic)
      .attr('font-size', '8px')
      .attr('font-style', 'italic')
      .attr('opacity', 0)
      .text('');

    // -- Animation cycle --
    let cancelled = false;
    let groupIndex = 0;

    function showGroup() {
      if (cancelled) return;

      const group = SPOTLIGHT_GROUPS[groupIndex % SPOTLIGHT_GROUPS.length];

      // Move spotlight
      spotlight.transition()
        .duration(500)
        .ease(d3.easeCubicInOut)
        .attr('cx', group.spotX)
        .attr('cy', group.spotY)
        .attr('opacity', 1);

      spotlightRing.transition()
        .duration(500)
        .ease(d3.easeCubicInOut)
        .attr('cx', group.spotX)
        .attr('cy', group.spotY)
        .attr('opacity', 0.5);

      // Category label
      categoryLabel
        .text(group.label)
        .attr('x', group.spotX)
        .attr('y', group.spotY + 55)
        .transition()
        .delay(300)
        .duration(300)
        .attr('opacity', 0.7);

      // Fade tokens: highlighted ones brighten, others dim
      tokenSels.forEach(({ text, highlight }, i) => {
        const isHighlighted = group.indices.includes(i);

        text.transition()
          .duration(400)
          .ease(d3.easeCubicInOut)
          .attr('opacity', isHighlighted ? 1 : 0.12)
          .attr('fill', isHighlighted ? COLORS.symbolic : 'var(--color-text-muted)')
          .attr('font-size', isHighlighted ? '12px' : '9px')
          .attr('font-weight', isHighlighted ? '700' : 'normal');

        if (isHighlighted) {
          // Show micro-label: "important", "relevant"
          const microLabels = ['selected', 'important', 'relevant'];
          const mIdx = group.indices.indexOf(i);
          highlight
            .text(microLabels[mIdx] || 'selected')
            .transition()
            .delay(400)
            .duration(300)
            .attr('opacity', 0.7);
        } else {
          highlight.transition().duration(200).attr('opacity', 0);
        }
      });

      // After hold, move to next group
      setTimeout(() => {
        if (cancelled) return;

        // Reset all tokens
        tokenSels.forEach(({ text, highlight }) => {
          text.transition()
            .duration(300)
            .attr('opacity', 0.5)
            .attr('fill', 'var(--color-text-muted)')
            .attr('font-size', '9px')
            .attr('font-weight', 'normal');
          highlight.transition().duration(200).attr('opacity', 0);
        });

        categoryLabel.transition().duration(200).attr('opacity', 0);

        groupIndex++;
        setTimeout(() => {
          if (!cancelled) showGroup();
        }, 600);
      }, 2800);
    }

    const startTimeout = setTimeout(showGroup, 500);

    return () => {
      cancelled = true;
      clearTimeout(startTimeout);
      svg.selectAll('*').interrupt();
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-eye" /> Attention Spotlight
      </h4>
      <svg ref={svgRef} viewBox="0 0 320 200" />
      <p className="viz-container__caption">What attention selects becomes real.</p>
    </div>
  );
}
